import type { ComputedRef } from "vue"
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import { useLocalStorage } from "@vueuse/core"
import { computed } from "vue"

/**
 * Custom composable to manage table column visibility and storage
 */
export function useTableColumns<T extends object>(
  allHeaders: ComputedRef<ITableHeader<T>[]>,
  storageKey: string,
  defaultVisibility?: Record<string, boolean>,
) {
  const initialVisibility = defaultVisibility ?? Object.fromEntries(
    allHeaders.value.map(header => [String(header?.value), true]),
  )

  const visibleColumns = useLocalStorage<Record<string, boolean>>(storageKey, initialVisibility)

  const headers = computed<ITableHeader<T>[]>(() => {
    return allHeaders.value.filter((header) => {
      return !!header?.value && visibleColumns.value[String(header.value)]
    })
  })

  const visibleColumnsCount = computed(() =>
    Object.values(visibleColumns.value).filter(Boolean).length,
  )

  function toggleColumn(columnValue: string) {
    visibleColumns.value[columnValue] = !visibleColumns.value[columnValue]
  }

  function showAllColumns() {
    allHeaders.value.forEach((header) => {
      if (header?.value) {
        visibleColumns.value[String(header.value)] = true
      }
    })
  }

  return {
    visibleColumns,
    headers,
    visibleColumnsCount,
    toggleColumn,
    showAllColumns,
  }
}
